import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Phone, Mail, ChevronRight } from "lucide-react";
import ProductCard from "../components/ProductCard";
import QuoteModal from "../components/QuoteModal";
import { products, company } from "../data/mock";

export default function ProductDetail() {
  const { id } = useParams();
  const product = products.find((p) => p.id === id);
  const [open, setOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  if (!product) {
    return (
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 text-center">
        <h1 className="text-3xl font-bold text-brand-navy">Product Not Found</h1>
        <Link to="/products" className="btn-primary-brand inline-flex mt-6 px-6 py-3 rounded-md text-sm font-semibold">View All Products</Link>
      </main>
    );
  }

  const related = products.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 4);
  const fillRelated = related.length < 4 ? [...related, ...products.filter(p => p.id !== product.id && !related.includes(p)).slice(0, 4 - related.length)] : related;

  const inquire = (p) => { setActiveProduct(p || product); setOpen(true); };

  return (
    <main>
      <section className="brand-navy text-white py-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold">{product.name}</h1>
          <nav className="text-sm text-white/60 mt-3 flex items-center gap-1.5">
            <Link to="/" className="hover:text-white">Home</Link> <ChevronRight size={12} />
            <Link to="/products" className="hover:text-white">Products</Link> <ChevronRight size={12} />
            <span className="text-white">{product.name}</span>
          </nav>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm">
            <img src={product.image} alt={product.name} className="w-full aspect-square object-cover" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-red font-semibold">{product.category}</p>
            <h2 className="text-3xl font-bold text-brand-navy mt-2 mb-4">{product.name}</h2>
            <div className="flex items-baseline gap-3 mb-5">
              <span className="text-3xl font-bold text-brand-red">{product.price}</span>
              <span className="text-sm text-gray-500">| Min. Order: {product.minOrder}</span>
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>

            <div className="bg-[#f7f8fc] rounded-md p-5 mb-6">
              <h3 className="text-base font-bold text-brand-navy mb-3">Specifications</h3>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(product.specs).map(([k, v]) => (
                    <tr key={k} className="border-b border-gray-200 last:border-0">
                      <td className="py-2 font-semibold text-brand-navy w-1/3">{k.replace(/_/g, " ")}</td>
                      <td className="py-2 text-gray-700">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="space-y-2 mb-6">
              {["Premium quality raw material", "Bulk orders & wholesale pricing", "Custom thickness & dimensions available", "Pan-India delivery with secure packaging"].map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                  <Check size={16} className="text-green-600" /> {b}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => inquire()} className="btn-red-brand px-7 py-3 rounded-md text-sm font-semibold">Send Inquiry</button>
              <a href={`tel:${company.phoneRaw}`} className="px-7 py-3 rounded-md text-sm font-semibold border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white transition-colors flex items-center gap-2">
                <Phone size={15} /> Call Now
              </a>
              <a href={`mailto:${company.email}`} className="px-7 py-3 rounded-md text-sm font-semibold border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white transition-colors flex items-center gap-2">
                <Mail size={15} /> Email
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {fillRelated.length > 0 && (
        <section className="bg-[#f7f8fc] py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-brand-navy mb-8">Products You May Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {fillRelated.map((p) => <ProductCard key={p.id} product={p} onInquire={(prod) => inquire(prod)} />)}
            </div>
          </div>
        </section>
      )}

      <QuoteModal open={open} onOpenChange={setOpen} productName={activeProduct?.name || product.name} />
    </main>
  );
}
